/**
 * Shared state and refs for the useChatApp modules (2026-09-25 gap closure).
 *
 * useChatApp.js was split into voice, location, session persistence, socket
 * and turn modules. They share one context object built here each render:
 * state values are this render's values, and setters, refs and endTurn are
 * stable, so memoized callbacks in the modules capture exactly what the
 * single-file hook captured.
 */
import { useState, useRef, useCallback } from 'react';
import { loadSessions, getOrCreateActiveSession } from '../../utils/chatSessions';
import { safeStorage } from '../../utils/safeStorage';
import { sanitizeMessagesForPersist } from '../../utils/confirmationCards';
import { placeFromMessages } from './chatConstants';

export function useChatState({ userId, authToken }) {
  const [sessions, setSessions] = useState(() => loadSessions(userId));
  const [sessionId, setSessionId] = useState(() => getOrCreateActiveSession(userId).id);
  const [messages, setMessages] = useState(() => {
    const active = getOrCreateActiveSession(userId);
    // A restored card is never confirmable, even if storage was edited to
    // say 'pending' (review B3 F1, 2026-09-29).
    return sanitizeMessagesForPersist(active.messages || []);
  });
  // Latest committed thread, for writes that must not wait for the persist
  // effect (it skips once a same-batch sign-out clears userId; pass 18).
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Synchronous mirror of isLoading: Enter and voice sends bypass the Send
  // button's disabled state, and two sends in one tick both saw
  // isLoading === false (2026-09-25 verification pass 2).
  const [isLocating, setIsLocating] = useState(false);
  const inFlightRef = useRef(false);
  // Bumped by every turn end (done, retire, new/select/delete chat). A
  // near-me turn waits on the browser location prompt before sending; its
  // callback drops the send if the epoch moved (2026-09-25 verification pass 3).
  const turnEpochRef = useRef(0);
  // True while a near-me turn waits on geolocation: nothing has been sent,
  // so leaving the chat must not force a reconnect.
  const awaitingLocationRef = useRef(false);
  // Bumped when the visible chat changes (new/select/delete/sign-in), so a
  // late location result is only kept for the chat that asked for it.
  const chatEpochRef = useRef(0);
  const locationWatchdogRef = useRef(null);
  const shareWatchdogRef = useRef(null);
  // Every location prompt (near-me or Share location) takes a number; only
  // the latest may touch isLocating or post a note (2026-09-25 pass 5 F4).
  const locationSeqRef = useRef(0);
  // Turn liveness timers (2026-09-25 adversarial pass 1): the no-`done`
  // watchdog and the error-without-done grace, armed by useChatSocket. Every
  // turn end goes through endTurn, so clearing them here covers done, retire,
  // unmount, chat switch and sign-out.
  const turnWatchdogRef = useRef(null);
  const errorGraceRef = useRef(null);
  // Set by useChatSocket each render; useChatTurn calls it after a send.
  const startTurnWatchdogRef = useRef(null);
  // C7 (2026-09-29): the current turn's tool_result envelopes. endTurn clears
  // them, so no turn end (done, retire, new/select chat, sign-out) can leak
  // them into another turn or chat; the citation attaches only to an assistant
  // message after this turn's user message.
  const turnEvidenceRef = useRef([]);
  // "Help me fill this" draft turn in flight (plan Track 5 B6, 2026-09-30):
  // { text, error, overflow, resolve } while the current turn is a report-draft
  // request, else null. While set, the turn's text/card frames are captured
  // here instead of entering the thread. `done` resolves it; any other turn end
  // (retire, drop, watchdog, chat switch, sign-out) settles it as interrupted
  // in endTurn below, so the caller's promise can never hang.
  const draftRef = useRef(null);
  const endTurn = useCallback(() => {
    const draft = draftRef.current;
    if (draft) {
      draftRef.current = null;
      draft.resolve({ ok: false, reason: draft.error ? 'agent-error' : 'interrupted' });
    }
    clearTimeout(turnWatchdogRef.current);
    clearTimeout(errorGraceRef.current);
    turnWatchdogRef.current = null;
    errorGraceRef.current = null;
    inFlightRef.current = false;
    if (awaitingLocationRef.current) {
      // Only the turn that owns the prompt resets the locating flag
      // (2026-09-25 pass 4 C3).
      clearTimeout(locationWatchdogRef.current);
      setIsLocating(false);
    }
    awaitingLocationRef.current = false;
    turnEpochRef.current += 1;
    // Drop any uncited evidence so it can never attach to a later turn/chat.
    turnEvidenceRef.current = [];
    setIsLoading(false);
  }, []);
  const [ws, setWs] = useState(null);
  const [connectionError, setConnectionError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [backendUrl, setBackendUrl] = useState(() => {
    return process.env.REACT_APP_WS_URL || 'ws://localhost:9000/ws';
  });
  const [clientLocation, setClientLocation] = useState(() => {
    const active = getOrCreateActiveSession(userId);
    return active.clientLocation || null;
  });
  /** Last place/zip named in this thread — reused for "my zip code". */
  const [sessionPlace, setSessionPlace] = useState(() => {
    const active = getOrCreateActiveSession(userId);
    return active.sessionPlace || placeFromMessages(active.messages) || null;
  });
  const [locationError, setLocationError] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  // Bumped whenever a recording is abandoned, so a transcription already in
  // flight is dropped instead of being sent into another chat or account.
  const voiceEpochRef = useRef(0); // bumped ONLY by abortVoiceRecording
  // Bumped by aborts and by new recordings: a spoken reply still loading
  // must not play over a new recording (pass 11 F1).
  const ttsEpochRef = useRef(0);
  const voiceStartingRef = useRef(false);
  const mediaRecorderRef = useRef(null);
  // Latest Guardian token for voice calls. Read through a ref so a token
  // change does not rebuild the WebSocket message handler (which would
  // reconnect the socket mid-turn).
  // Updated during render, not in an effect: a 401 landing before the
  // passive-effect flush must already see the new token (pass 17).
  const authTokenRef = useRef(authToken);
  authTokenRef.current = authToken;
  const audioChunksRef = useRef([]);
  const audioPlayerRef = useRef(null);
  const expectAwosReplyRef = useRef(false);
  const lastAssistantTextRef = useRef('');
  // Confirmation card in flight (see utils/confirmationCards.js): set by
  // confirmReport(), cleared and resolved when the matching `done` arrives.
  const submittingConfirmationIdRef = useRef(null);
  // connectWebSocket is defined after the message handler that needs it.
  const reconnectRef = useRef(null);
  // Per-card expiry timers, cleared on unmount so a stale closure never
  // fires setMessages after the hook is gone.
  const confirmationExpiryTimersRef = useRef([]);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return safeStorage.get('guardianSidebarCollapsed') === 'true';
  });
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const sessionIdRef = useRef(sessionId);
  const skipPersistRef = useRef(false);
  // Which user the in-memory thread was loaded for. On a direct A -> B
  // switch the persist effect runs before the userId effect and would write
  // A's thread into B's storage; it may only persist the owner's own thread
  // (2026-09-25 verification pass 9 F1).
  const loadedForUserRef = useRef(null);
  // Latest sendMessage, set each render by useChatTurn: a transcript must use
  // the current socket and thread, not the ones from when recording began.
  const sendMessageRef = useRef(null);

  return {
    sessions, setSessions, sessionId, setSessionId, messages, setMessages, messagesRef,
    inputMessage, setInputMessage, isConnected, setIsConnected, isLoading, setIsLoading,
    isLocating, setIsLocating, inFlightRef, turnEpochRef, awaitingLocationRef, chatEpochRef,
    locationWatchdogRef, shareWatchdogRef, locationSeqRef, endTurn,
    turnEvidenceRef, draftRef,
    ws, setWs, connectionError, setConnectionError, isRetrying, setIsRetrying,
    backendUrl, setBackendUrl, clientLocation, setClientLocation, sessionPlace, setSessionPlace,
    locationError, setLocationError, isRecording, setIsRecording, voiceError, setVoiceError,
    voiceEpochRef, ttsEpochRef, voiceStartingRef, mediaRecorderRef, authTokenRef, audioChunksRef,
    audioPlayerRef, expectAwosReplyRef, lastAssistantTextRef, submittingConfirmationIdRef,
    reconnectRef, confirmationExpiryTimersRef, showUrlInput, setShowUrlInput,
    sidebarCollapsed, setSidebarCollapsed, messagesEndRef, wsRef, sessionIdRef,
    skipPersistRef, loadedForUserRef, sendMessageRef,
    turnWatchdogRef, errorGraceRef, startTurnWatchdogRef,
  };
}
